import {FormControl, FormField, showNotification} from 'platform/components';
import {Grid} from 'platform/foundation';

import {useWatch, UseFormReturn} from 'react-hook-form';

import {equals, isEmpty, isNil} from 'ramda';

import {PurchasePriceRuleScope, useLazyGetTrimLevelsQuery} from '@dms/api/sales';
import {i18n} from '@dms/i18n';

import {buildPurchasePriceRuleName} from '../../../utils/buildPurchasePriceRuleName';
import {getSinglePurchasePriceRuleMake} from '../../../utils/getSinglePurchasePriceRuleMake';
import {usePurchasePriceRuleScopeOptions} from '../hooks/usePurchasePriceRuleScopeOptions';
import {PurchasePriceRuleFormValues} from '../PurchasePriceRuleFormSchema';
import {PurchasePriceRuleFormSection} from './PurchasePriceRuleFormSection';
import {PurchasePriceRuleScopeSummary} from './PurchasePriceRuleScopeSummary';

interface PurchasePriceRuleScopeSectionProps {
  control: FormControl<PurchasePriceRuleFormValues>;
  formApi: UseFormReturn<PurchasePriceRuleFormValues>;
  /** Fills the name from the scope while the user has not named the rule. */
  shouldSuggestName: boolean;
  baseTestId: (suffix: string) => string;
}

type ChoiceValue = (string | number)[] | null;

const toKeys = (value: ChoiceValue) => (value ?? []).map((key) => key.toString());

const LABEL_SEPARATOR = ', ';

export function PurchasePriceRuleScopeSection(props: PurchasePriceRuleScopeSectionProps) {
  const [makesValue, modelFamiliesValue, trimsValue] = useWatch({
    control: props.control,
    name: ['scope.makes', 'scope.modelFamilies', 'scope.trims'],
  });

  const makes = makesValue ?? [];
  const modelFamilies = modelFamiliesValue ?? [];
  const trims = trimsValue ?? [];

  const options = usePurchasePriceRuleScopeOptions(makes, modelFamilies);
  const [getTrimLevels] = useLazyGetTrimLevelsQuery();

  const optionLabels = new Map(
    [...options.makeOptions, ...options.modelFamilyOptions, ...options.trimOptions].map(
      (option) => [option.value.toString(), option.label]
    )
  );

  const suggestName = (scope: PurchasePriceRuleScope) => {
    if (!props.shouldSuggestName) {
      return;
    }

    props.formApi.setValue(
      'name',
      buildPurchasePriceRuleName({scope, getLabel: (key) => optionLabels.get(key) ?? key}),
      {shouldDirty: true, shouldValidate: props.formApi.formState.isSubmitted}
    );
  };

  // Model families and trims belong to one make, so they are cleared as soon as the selection no
  // longer is exactly the same single make.
  const handleMakesChange = (value: ChoiceValue) => {
    const nextMakes = toKeys(value);
    const nextMake = getSinglePurchasePriceRuleMake(nextMakes);
    const shouldClearNarrowing =
      isNil(nextMake) || nextMake !== getSinglePurchasePriceRuleMake(makes);

    if (shouldClearNarrowing) {
      props.formApi.setValue('scope.modelFamilies', [], {shouldDirty: true});
      props.formApi.setValue('scope.trims', [], {shouldDirty: true});
    }

    suggestName({
      makes: nextMakes,
      modelFamilies: shouldClearNarrowing ? [] : modelFamilies,
      trims: shouldClearNarrowing ? [] : trims,
    });
  };

  // Trims are offered per model family. Selected trims that the new families no longer offer would
  // stay hidden in the rule, so they are dropped and the user is told which ones.
  const dropUnavailableTrims = (make: string, nextModelFamilies: string[]) =>
    getTrimLevels({make, modelFamilies: nextModelFamilies}, true)
      .unwrap()
      .then((availableTrims) => {
        const currentScope = props.formApi.getValues('scope');
        const currentTrims = currentScope.trims ?? [];

        // A newer change of the scope wins over this response.
        if (!equals(currentScope.modelFamilies ?? [], nextModelFamilies)) {
          return;
        }

        const availableKeys = new Set(availableTrims.map((trim) => trim.value));
        const keptTrims = currentTrims.filter((key) => availableKeys.has(key));
        const droppedTrims = currentTrims.filter((key) => !availableKeys.has(key));

        if (isEmpty(droppedTrims)) {
          return;
        }

        props.formApi.setValue('scope.trims', keptTrims, {shouldDirty: true});
        suggestName({makes, modelFamilies: nextModelFamilies, trims: keptTrims});
        showNotification.warning(
          i18n.t('page.settings.purchasePrice.ruleEditor.scope.unavailableTrimsRemoved', {
            trims: droppedTrims.map((key) => optionLabels.get(key) ?? key).join(LABEL_SEPARATOR),
          })
        );
      })
      // Without the catalogue the selection stays as it is; the trims field shows the load error.
      .catch(() => undefined);

  const handleModelFamiliesChange = (value: ChoiceValue) => {
    const nextModelFamilies = toKeys(value);
    const make = getSinglePurchasePriceRuleMake(makes);

    suggestName({makes, modelFamilies: nextModelFamilies, trims});

    if (isNil(make) || isEmpty(trims)) {
      return;
    }

    dropUnavailableTrims(make, nextModelFamilies);
  };

  const handleTrimsChange = (value: ChoiceValue) =>
    suggestName({makes, modelFamilies, trims: toKeys(value)});

  return (
    <PurchasePriceRuleFormSection
      title={i18n.t('page.settings.purchasePrice.ruleEditor.scope.title')}
      description={i18n.t('page.settings.purchasePrice.ruleEditor.scope.description')}
      data-testid={props.baseTestId('scopeSection')}
    >
      <Grid columns={3} spacing={4}>
        <FormField
          control={props.control}
          name="scope.makes"
          type="multiChoice"
          label={i18n.t('page.settings.purchasePrice.ruleEditor.scope.makes')}
          options={options.makeOptions}
          placeholder={i18n.t('general.labels.selectOption')}
          isLoading={options.isMakesLoading}
          onChange={handleMakesChange}
          isRequired
          data-testid={props.baseTestId('makes')}
        />
        <FormField
          control={props.control}
          name="scope.modelFamilies"
          type="multiChoice"
          label={i18n.t('page.settings.purchasePrice.ruleEditor.scope.modelFamilies')}
          options={options.modelFamilyOptions}
          placeholder={i18n.t('page.settings.purchasePrice.marginRules.scope.allModelFamilies')}
          isLoading={options.isMakesLoading}
          isDisabled={!options.hasSingleMake}
          onChange={handleModelFamiliesChange}
          data-testid={props.baseTestId('modelFamilies')}
        />
        <FormField
          control={props.control}
          name="scope.trims"
          type="multiChoice"
          label={i18n.t('page.settings.purchasePrice.ruleEditor.scope.trims')}
          options={options.trimOptions}
          placeholder={i18n.t('page.settings.purchasePrice.marginRules.scope.allTrims')}
          isLoading={options.isTrimsLoading}
          isDisabled={!options.hasSingleMake}
          onChange={handleTrimsChange}
          data-testid={props.baseTestId('trims')}
        />
      </Grid>

      <PurchasePriceRuleScopeSummary
        scope={{makes, modelFamilies, trims}}
        data-testid={props.baseTestId('scopeSummary')}
      />
    </PurchasePriceRuleFormSection>
  );
}
